/**
 * Voice input — the pure half of the mic button (search bar + Αριάδνη chat).
 *
 * We use the BROWSER's own speech recognition (`SpeechRecognition` /
 * `webkitSpeechRecognition`), the same approach as Findbar: Chrome hands the
 * audio to Google, Safari to Apple, and we only ever receive text. No audio
 * touches our servers and there is no per-use cost. Decision a3b47b79.
 *
 * Everything here is a pure function so it can be unit-tested — the live
 * recognition object cannot be driven from an automated browser (it needs a
 * real microphone and a permission grant).
 */

/** The subset of a SpeechRecognitionResult we read. */
export interface TranscriptPart {
  transcript: string;
  isFinal: boolean;
}

/**
 * Can this page use voice input at all?
 *
 * Two independent conditions, and the second is the one competitors miss:
 *  1. the browser implements speech recognition (Chrome, Edge, Safari — not
 *     Firefox);
 *  2. the HOST page lets us use the microphone. The widget runs inside the
 *     host's document (Shadow DOM, not an iframe), so the host's
 *     `Permissions-Policy` header applies to us. parousiasi.gr and digital4u's
 *     beta hosts send `microphone=()`; there, recognition fails instantly with
 *     `not-allowed` and never even prompts — which is exactly what Findbar's
 *     mic button does on parousiasi.gr (measured 2026-09-16). We hide the
 *     button instead of shipping a button that can only fail.
 *
 * `allowsFeature` is Chromium-only. Where it is missing we cannot tell, so we
 * allow the button and let the error path explain any refusal.
 */
export function canUseVoiceInput(
  win: { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown } | undefined,
  doc: { permissionsPolicy?: PolicyLike; featurePolicy?: PolicyLike } | undefined,
): boolean {
  if (!win || !doc) return false;
  if (!getSpeechRecognitionCtor(win)) return false;
  return micAllowedByPolicy(doc);
}

interface PolicyLike {
  allowsFeature?: (feature: string) => boolean;
}

export function micAllowedByPolicy(doc: { permissionsPolicy?: PolicyLike; featurePolicy?: PolicyLike }): boolean {
  const policy = doc.permissionsPolicy ?? doc.featurePolicy;
  if (!policy || typeof policy.allowsFeature !== 'function') return true;
  try {
    return policy.allowsFeature('microphone');
  } catch {
    return true;
  }
}

export function getSpeechRecognitionCtor(win: { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown }): unknown {
  const ctor = win.SpeechRecognition ?? win.webkitSpeechRecognition;
  return typeof ctor === 'function' ? ctor : null;
}

/**
 * The recognition language, from the client's catalogue language (the
 * Meilisearch locale on the config — `ell` for every client today).
 */
export function speechLangForLocale(locale: string | undefined): string {
  switch ((locale || '').toLowerCase()) {
    case 'eng':
    case 'en':
      return 'en-US';
    default:
      return 'el-GR';
  }
}

/**
 * Join the recognition results of one listening session into the text to show.
 *
 * Browsers disagree on what `results` holds in continuous mode:
 *  - desktop Chrome / Safari: one entry per phrase — «ποτήρια», «κρασιού» —
 *    which must be JOINED;
 *  - Android Chrome: each entry repeats everything so far — «ποτήρια»,
 *    «ποτήρια κρασιού» — so joining would say everything twice.
 * An entry that CONTINUES the previous one replaces it; anything else is
 * appended (see `continuesPhrase`). Whitespace is normalised because Chrome
 * prefixes later phrases with a space.
 */
export function assembleTranscript(parts: TranscriptPart[]): string {
  const segments: string[] = [];
  for (const part of parts) {
    const text = part.transcript.replace(/\s+/g, ' ').trim();
    if (!text) continue;
    const last = segments[segments.length - 1];
    if (last !== undefined && continuesPhrase(last, text)) {
      segments[segments.length - 1] = text;
    } else {
      segments.push(text);
    }
  }
  return segments.join(' ');
}

/** Lowercase, no accents, no punctuation, single spaces — for COMPARING only. */
function comparable(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Is `next` the same phrase as `prev`, resent with more words or corrections?
 *  - equal, or `prev` + more words («ποτήρια» → «ποτήρια κρασιού»);
 *  - ignoring accents, case and punctuation (Safari may add «.»);
 *  - a resend whose earlier words were REVISED: at least as long, same first
 *    word, and at least half of `prev`'s words still leading it
 *    («ποτήρια κρασί» → «ποτήρια κρασιού έξι»).
 * Whole words only: «σετ» then «σετάκι» are two phrases.
 */
export function continuesPhrase(prev: string, next: string): boolean {
  const p = comparable(prev);
  const n = comparable(next);
  if (!p) return true;
  if (n === p || n.startsWith(p + ' ')) return true;
  const pw = p.split(' ');
  const nw = n.split(' ');
  if (nw.length < pw.length || nw[0] !== pw[0]) return false;
  let shared = 0;
  while (shared < pw.length && pw[shared] === nw[shared]) shared++;
  return shared >= Math.ceil(pw.length / 2);
}

export type VoiceErrorKind = 'no-speech' | 'no-mic' | 'denied' | 'network' | 'unavailable';

/**
 * Map a SpeechRecognition `error` code to what we tell the shopper.
 * `aborted` is our own cancel → say nothing.
 * `hideButton` marks failures that retrying can never fix in this browser
 * (e.g. Brave exposes the API but has no speech service → `network`), so the
 * mic disappears for the rest of the visit instead of failing on every press.
 */
export function classifyVoiceError(code: string | undefined): { kind: VoiceErrorKind; hideButton: boolean } | null {
  switch (code) {
    case 'aborted':
      return null;
    case 'no-speech':
      return { kind: 'no-speech', hideButton: false };
    case 'audio-capture':
      return { kind: 'no-mic', hideButton: false };
    case 'not-allowed':
      return { kind: 'denied', hideButton: false };
    // Brave fails like this every time; Chrome only on a real connection
    // blip — useVoiceInput hides the mic only after a second one.
    case 'network':
      return { kind: 'network', hideButton: true };
    case 'service-not-allowed':
    case 'language-not-supported':
      return { kind: 'unavailable', hideButton: true };
    default:
      return { kind: 'unavailable', hideButton: false };
  }
}

export interface VoiceStrings {
  listening: string;
  start: string;
  stop: string;
  errors: Record<VoiceErrorKind, string>;
}

export function voiceStringsForLang(lang: string): VoiceStrings {
  if (lang.startsWith('en')) {
    return {
      listening: 'Listening…',
      start: 'Voice search',
      stop: 'Stop listening',
      errors: {
        'no-speech': "We didn't hear anything — try again",
        'no-mic': 'No microphone found',
        denied: 'Microphone access was not allowed',
        network: 'Voice search is not available right now',
        unavailable: 'Voice search is not available in this browser',
      },
    };
  }
  return {
    listening: 'Μιλήστε τώρα…',
    start: 'Φωνητική αναζήτηση',
    stop: 'Διακοπή ακρόασης',
    errors: {
      'no-speech': 'Δεν ακούστηκε κάτι — δοκιμάστε ξανά',
      'no-mic': 'Δεν βρέθηκε μικρόφωνο',
      denied: 'Δεν δόθηκε άδεια χρήσης του μικροφώνου',
      network: 'Η φωνητική αναζήτηση δεν είναι διαθέσιμη αυτή τη στιγμή',
      unavailable: 'Η φωνητική αναζήτηση δεν είναι διαθέσιμη σε αυτόν τον browser',
    },
  };
}
